/**
 * Client-local WebGL / GPU helpers for browser games.
 * Games render on the user's GPU via WebGL — never frame-by-frame on the ticktock server GPU.
 *
 * Ticktock host GPU (inventory 2026-09): AMD Radeon RX 470/480/570/580 class
 * (Ellesmere, ~8 GB VRAM, amdgpu + Mesa RADV). No NVIDIA / no CUDA.
 * Wired today: Ollama with OLLAMA_VULKAN=1. Not installed: Sunshine / Moonlight cloud stream.
 */

import type { WebGLRenderer } from "three";
import * as THREE from "three";

/** Short UI copy — server GPU is for AI/bake, not live WebGL frames. */
export const SERVER_GPU_NOTE =
  "Server GPU: available for X (AMD Radeon 8 GB · Ollama/Vulkan AI & baking — not live game frames).";

export type GraphicsQuality = "low" | "medium" | "high";

export type WebGlProbe = {
  ok: boolean;
  webgl2: boolean;
  renderer: string;
  vendor: string;
  message?: string;
};

/** Desktop defaults High; phones/tablets Medium (user can raise). */
export function defaultGraphicsQuality(): GraphicsQuality {
  if (typeof window === "undefined") return "high";
  const coarse =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  const narrow = window.innerWidth > 0 && window.innerWidth < 900;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (coarse || narrow || (typeof mem === "number" && mem > 0 && mem <= 4)) {
    return "medium";
  }
  return "high";
}

/** DPR range for R3F Canvas — High uses full devicePixelRatio (capped). */
export function dprForQuality(quality: GraphicsQuality): [number, number] {
  if (typeof window === "undefined") {
    return quality === "high" ? [1, 2] : quality === "medium" ? [1, 1.5] : [1, 1];
  }
  const native = Math.max(1, window.devicePixelRatio || 1);
  const mobile =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  if (quality === "low") return [1, 1];
  if (quality === "medium") return [1, Math.min(mobile ? 1.35 : 1.5, native)];
  // High: push local GPU resolution; soft cap on mobile to avoid thermal melt
  return [1, Math.min(mobile ? 1.75 : 2.5, native)];
}

/**
 * R3F `gl` props — requests discrete / high-performance GPU when available.
 * WebGL2 is preferred by three.js when the context supports it.
 */
export function canvasGlProps(
  quality: GraphicsQuality,
  opts?: { forceHighPerf?: boolean }
): {
  antialias: boolean;
  powerPreference: WebGLPowerPreference;
  failIfMajorPerformanceCaveat?: boolean;
  alpha: boolean;
} {
  const highPerf =
    opts?.forceHighPerf === true ||
    quality === "high" ||
    quality === "medium";
  return {
    antialias: quality !== "low",
    powerPreference: highPerf ? "high-performance" : "default",
    // Prefer real GPU; if software rasterizer only, still try (message via probe)
    alpha: false,
  };
}

export function shadowMapSize(quality: GraphicsQuality): number {
  if (quality === "high") return 2048;
  if (quality === "medium") return 1024;
  return 512;
}

export function anisotropyForQuality(quality: GraphicsQuality): number {
  if (quality === "high") return 8;
  if (quality === "medium") return 4;
  return 1;
}

/** Apply ACES + soft shadows after WebGLRenderer exists (local GPU). */
export function applyRendererGpuTune(
  gl: WebGLRenderer,
  quality: GraphicsQuality,
  opts?: { exposure?: number }
): void {
  gl.toneMapping = THREE.ACESFilmicToneMapping;
  gl.toneMappingExposure =
    opts?.exposure ?? (quality === "high" ? 1.12 : quality === "medium" ? 1.0 : 0.92);
  gl.shadowMap.enabled = quality !== "low";
  gl.shadowMap.type =
    quality === "high" ? THREE.PCFSoftShadowMap : THREE.BasicShadowMap;
  // Prefer WebGL2 capabilities when present
  const maxAni = gl.capabilities.getMaxAnisotropy?.() ?? 1;
  (gl as WebGLRenderer & { userData: { maxAnisotropy: number } }).userData = {
    ...((gl as { userData?: object }).userData || {}),
    maxAnisotropy: maxAni,
  };
}

export function applyTextureAnisotropy(
  tex: THREE.Texture,
  quality: GraphicsQuality,
  maxFromGpu?: number
): void {
  const want = anisotropyForQuality(quality);
  tex.anisotropy = Math.min(want, maxFromGpu && maxFromGpu > 0 ? maxFromGpu : want);
  tex.needsUpdate = true;
}

/** Probe local WebGL — used for fail message, not server rendering. */
export function probeWebGL(): WebGlProbe {
  if (typeof document === "undefined") {
    return { ok: false, webgl2: false, renderer: "", vendor: "", message: "No DOM" };
  }
  try {
    const canvas = document.createElement("canvas");
    const gl2 = canvas.getContext("webgl2", {
      powerPreference: "high-performance",
      antialias: true,
    }) as WebGL2RenderingContext | null;
    if (gl2) {
      const dbg = gl2.getExtension("WEBGL_debug_renderer_info");
      const renderer = dbg
        ? String(gl2.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || "")
        : "";
      const vendor = dbg
        ? String(gl2.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || "")
        : "";
      return { ok: true, webgl2: true, renderer, vendor };
    }
    const gl1 = canvas.getContext("webgl", {
      powerPreference: "high-performance",
    }) as WebGLRenderingContext | null;
    if (gl1) {
      const dbg = gl1.getExtension("WEBGL_debug_renderer_info");
      const renderer = dbg
        ? String(gl1.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || "")
        : "";
      const vendor = dbg
        ? String(gl1.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || "")
        : "";
      return {
        ok: true,
        webgl2: false,
        renderer,
        vendor,
        message: "WebGL1 only — upgrade browser for best graphics",
      };
    }
    return {
      ok: false,
      webgl2: false,
      renderer: "",
      vendor: "",
      message:
        "WebGL is unavailable. Enable hardware acceleration in your browser so games can use your local GPU.",
    };
  } catch {
    return {
      ok: false,
      webgl2: false,
      renderer: "",
      vendor: "",
      message: "WebGL probe failed. Check GPU drivers / browser hardware acceleration.",
    };
  }
}

export function parseGraphicsQuality(
  v: unknown,
  fallback: GraphicsQuality = "high"
): GraphicsQuality {
  if (v === "low" || v === "medium" || v === "high") return v;
  return fallback;
}
